import { motion } from "framer-motion";
import styled, { css } from "styled-components";

import Button from "../../components/Buttons/Button";
import { pxToRem } from "../../styles/utils";
import { PageHeader } from "./studentPortal.styles";

export const CalendarPageShell = styled.div`
  display: flex;
  min-height: calc(var(--real-vh) - ${pxToRem(80)});
  flex-direction: column;
  gap: ${pxToRem(14)};

  @media (max-width: 900px) {
    min-height: 0;
  }
`;

export const CalendarPageHeader = styled(PageHeader)`
  margin-bottom: 0;
`;

export const CalendarWorkspace = styled.div`
  position: relative;
  display: flex;
  min-width: 0;
  min-height: 0;
  flex: 1;
  align-items: stretch;
  gap: ${pxToRem(14)};

  @media (max-width: 900px) {
    display: block;
  }
`;

export const CalendarMainMotion = styled(motion.div)`
  display: flex;
  min-width: 0;
  min-height: 0;
  flex: 1;
`;

export const CalendarHeaderMetrics = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${pxToRem(5)} ${({ theme }) => pxToRem(theme.spacing.sm)};
  flex-wrap: wrap;
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${pxToRem(11)};
  line-height: 1.2;
`;

export const CalendarMetric = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: ${({ theme }) => pxToRem(theme.spacing.xxs)};
  white-space: nowrap;

  strong {
    color: ${({ theme }) => theme.colors.ink};
    font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
    font-weight: 700;
  }
`;

export const CalendarLoading = styled.span`
  color: ${({ theme }) => theme.colors.inkMuted};
  white-space: nowrap;
`;

export const CalendarDaySlot = styled.div`
  min-width: 0;
  min-height: ${pxToRem(88)};
`;

export const StudentCalendarDay = styled.button<{
  $adjacent: boolean;
  $today: boolean;
  $hasEvents: boolean;
  $selected: boolean;
}>`
  display: flex;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  flex-direction: column;
  gap: ${({ theme }) => pxToRem(theme.spacing.xxs)};
  overflow: hidden;
  padding: ${pxToRem(7)} ${({ theme }) => pxToRem(theme.spacing.xs)};
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  appearance: none;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  color: inherit;
  cursor: ${({ $hasEvents }) => ($hasEvents ? "pointer" : "default")};
  font: inherit;
  text-align: left;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    background 0.15s ease,
    transform 0.15s ease;

  ${({ $hasEvents, $adjacent, theme }) =>
    $hasEvents &&
    !$adjacent &&
    css`
      border-color: ${theme.colors.brandMedium};

      &:hover {
        border-color: ${theme.colors.brandStrong};
        background: ${theme.colors.brandSubtle};
        transform: translateY(${pxToRem(-1)});
      }
    `}

  ${({ $today, $adjacent, theme }) =>
    $today &&
    !$adjacent &&
    css`
      border-color: ${theme.colors.brand};
      box-shadow: inset 0 0 0 ${pxToRem(1)} ${theme.colors.brand};
    `}

  ${({ $selected, $adjacent, theme }) =>
    $selected &&
    !$adjacent &&
    css`
      border-color: ${theme.colors.brand};
      background: ${theme.colors.brandSubtle};
      box-shadow: ${theme.shadows.sm}, inset 0 0 0 ${pxToRem(1)} ${theme.colors.brand};
      transform: translateY(${pxToRem(-1)});
    `}

  ${({ $adjacent, theme }) =>
    $adjacent &&
    css`
      border-style: dashed;
      border-color: ${theme.colors.borderDark};
      background: ${theme.colors.surfaceFaint};
      opacity: 0.55;
    `}

  &:disabled {
    cursor: default;
  }

  &:focus-visible {
    outline: none;
    border-color: ${({ theme }) => theme.colors.brand};
    box-shadow: ${({ theme }) => theme.shadows.sm}, 0 0 0 ${pxToRem(2)} ${({ theme }) => theme.colors.brandMedium};
  }
`;

export const CalendarDayTop = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: ${pxToRem(6)};
  flex-shrink: 0;
`;

export const CalendarDayDate = styled.div`
  display: inline-flex;
  min-width: 0;
  flex-shrink: 0;
  align-items: baseline;
  gap: ${({ theme }) => pxToRem(theme.spacing.xxs)};
`;

export const CalendarDayNumber = styled.span<{ $adjacent: boolean }>`
  flex-shrink: 0;
  color: ${({ theme, $adjacent }) => ($adjacent ? theme.colors.inkMuted : theme.colors.ink)};
  font-size: ${pxToRem(18)};
  font-weight: 700;
  line-height: 1;
`;

export const CalendarDayMonth = styled.span`
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${pxToRem(11)};
  font-weight: 600;
  line-height: 1;
  text-transform: lowercase;
`;

export const CalendarDayState = styled.span`
  min-width: 0;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${pxToRem(9.5)};
  font-weight: 600;
  line-height: 1.1;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CalendarTodayBadge = styled.span`
  display: inline-flex;
  height: ${pxToRem(17)};
  flex-shrink: 0;
  align-items: center;
  padding: 0 ${pxToRem(6)};
  border-radius: ${({ theme }) => pxToRem(theme.radii.pill)};
  background: ${({ theme }) => theme.colors.brandSoft};
  color: ${({ theme }) => theme.colors.brandDark};
  font-size: ${pxToRem(9)};
  font-weight: 700;
  line-height: 1;
`;

export const DayEntries = styled.div`
  display: flex;
  min-height: 0;
  flex-direction: column;
  gap: ${pxToRem(3)};
  overflow: hidden;
`;

export const DayEvent = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: ${pxToRem(1)};
  padding: ${pxToRem(3)} ${pxToRem(5)};
  overflow: hidden;
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.divider};
  border-radius: ${({ theme }) => pxToRem(theme.radii.sm)};
  background: ${({ theme }) => theme.colors.surfaceCool};
`;

export const DayEventTop = styled.div`
  display: flex;
  min-width: 0;
  align-items: baseline;
  gap: ${({ theme }) => pxToRem(theme.spacing.xxs)};
`;

export const DayEventTime = styled.span`
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.brandDark};
  font-size: ${pxToRem(9.5)};
  font-weight: 700;
  line-height: 1.2;
`;

export const DayEventCourse = styled.span`
  min-width: 0;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${pxToRem(9.5)};
  font-weight: 600;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const DayEventDescription = styled.span`
  min-width: 0;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${pxToRem(9)};
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const MoreEvents = styled.span`
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${pxToRem(9.5)};
  font-weight: 600;
  line-height: 1.2;
`;

export const CalendarFooterMain = styled.div`
  display: flex;
  min-width: 0;
  flex: 1;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => pxToRem(theme.spacing.xs)} ${pxToRem(14)};
  flex-wrap: wrap;
`;

export const MonthControls = styled.div`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: ${pxToRem(6)};
`;

export const MonthNavButton = styled(Button)`
  width: ${({ theme }) => pxToRem(theme.controlHeight.sm)};
  min-width: ${({ theme }) => pxToRem(theme.controlHeight.sm)};
  max-width: ${({ theme }) => pxToRem(theme.controlHeight.sm)};
  height: ${({ theme }) => pxToRem(theme.controlHeight.sm)};
  padding: 0;
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xl)};
  font-weight: 400;
  line-height: 1;
`;

export const MonthDisplay = styled.div`
  display: inline-flex;
  width: ${pxToRem(168)};
  height: ${({ theme }) => pxToRem(theme.controlHeight.sm)};
  align-items: center;
  justify-content: center;
  padding: 0 ${({ theme }) => pxToRem(theme.spacing.sm)};
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
`;

export const CurrentMonthButton = styled(Button)`
  width: auto;
  min-width: ${pxToRem(96)};
  max-width: none;
  height: ${({ theme }) => pxToRem(theme.controlHeight.sm)};
  padding: 0 ${pxToRem(14)};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
`;

export const CalendarFooterHint = styled.span`
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${pxToRem(11)};
  line-height: 1.3;
`;

export const CalendarDrawerMotion = styled(motion.div)`
  display: flex;
  width: 0;
  min-width: 0;
  min-height: 0;
  flex-shrink: 0;
  overflow: hidden;

  @media (max-width: 900px) {
    position: fixed;
    z-index: 1201;
    right: ${pxToRem(12)};
    bottom: ${pxToRem(12)};
    left: ${pxToRem(12)};
    width: auto !important;
    height: min(${pxToRem(620)}, calc(var(--real-vh) - ${pxToRem(32)}));
    max-height: calc(var(--real-vh) - ${pxToRem(32)});
    overflow: visible;
  }
`;

export const MobileDrawerBackdrop = styled(motion.button)`
  position: fixed;
  z-index: 1200;
  inset: 0;
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  background: ${({ theme }) => theme.colors.shadowStrong};
  cursor: pointer;
`;

export const CalendarDrawerPanel = styled.aside`
  display: flex;
  width: ${pxToRem(510)};
  min-width: ${pxToRem(510)};
  min-height: 0;
  flex: 1;
  flex-direction: column;
  overflow: hidden;
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.lg)};
  background: ${({ theme }) => theme.colors.white};
  box-shadow: ${({ theme }) => theme.shadows.md};

  @media (max-width: 900px) {
    width: 100%;
    min-width: 0;
    border-radius: ${({ theme }) => pxToRem(theme.radii.lg)};
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
`;

export const DrawerHeader = styled.header`
  display: flex;
  flex-shrink: 0;
  align-items: flex-start;
  gap: ${({ theme }) => pxToRem(theme.spacing.sm)};
  padding: ${({ theme }) => `${pxToRem(theme.spacing.md)} ${pxToRem(theme.spacing.lg)}`};
  border-bottom: ${pxToRem(1)} solid ${({ theme }) => theme.borders.divider};
`;

export const DrawerHeaderCopy = styled.div`
  min-width: 0;
  flex: 1;
`;

export const DrawerEyebrow = styled.div`
  margin-bottom: ${({ theme }) => pxToRem(theme.spacing.xxs)};
  color: ${({ theme }) => theme.colors.brandDark};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
  font-weight: 700;
  line-height: 1.2;
`;

export const DrawerTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.lg)};
  font-weight: 700;
  line-height: 1.2;
`;

export const DrawerSubtitle = styled.div`
  margin-top: ${({ theme }) => pxToRem(theme.spacing.xxs)};
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  line-height: 1.35;
`;

export const DrawerCloseButton = styled.button`
  display: inline-flex;
  width: ${({ theme }) => pxToRem(theme.controlHeight.sm)};
  height: ${({ theme }) => pxToRem(theme.controlHeight.sm)};
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.inkMuted};
  cursor: pointer;
  transition:
    color 0.15s ease,
    border-color 0.15s ease,
    background 0.15s ease;

  svg {
    width: ${({ theme }) => pxToRem(theme.fontSize.md)};
    height: ${({ theme }) => pxToRem(theme.fontSize.md)};
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.brandStrong};
    background: ${({ theme }) => theme.colors.brandSubtle};
    color: ${({ theme }) => theme.colors.brandDark};
  }

  &:focus-visible {
    outline: none;
    border-color: ${({ theme }) => theme.colors.brand};
    box-shadow: 0 0 0 ${pxToRem(2)} ${({ theme }) => theme.colors.brandMedium};
  }
`;

export const DrawerContent = styled(motion.div)`
  min-height: 0;
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => pxToRem(theme.spacing.lg)};

  &::-webkit-scrollbar {
    width: ${pxToRem(6)};
  }

  &::-webkit-scrollbar-thumb {
    border-radius: ${({ theme }) => pxToRem(theme.radii.pill)};
    background: ${({ theme }) => theme.colors.borderDark};
  }
`;

export const DrawerEventList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => pxToRem(theme.spacing.sm)};
`;

export const DrawerEventCard = styled.article`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => pxToRem(theme.spacing.sm)};
  padding: ${({ theme }) => pxToRem(theme.spacing.md)};
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  background: ${({ theme }) => theme.colors.surfaceCool};
`;

export const DrawerEventHeader = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: ${({ theme }) => pxToRem(theme.spacing.xs)};
`;

export const DrawerEventTime = styled.span`
  display: inline-flex;
  min-height: ${pxToRem(24)};
  flex-shrink: 0;
  align-items: center;
  padding: 0 ${({ theme }) => pxToRem(theme.spacing.xs)};
  border-radius: ${({ theme }) => pxToRem(theme.radii.pill)};
  background: ${({ theme }) => theme.colors.brandSoft};
  color: ${({ theme }) => theme.colors.brandDark};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
  font-weight: 700;
  line-height: 1;
`;

export const DrawerEventCourse = styled.div`
  min-width: 0;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  font-weight: 700;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const DrawerEventDescription = styled.div`
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  line-height: 1.45;
  white-space: pre-wrap;
`;

export const DrawerCourseButton = styled(Button)`
  width: auto;
  min-width: 0;
  max-width: max-content;
  align-self: flex-start;
`;

export const VisuallyHidden = styled.span`
  position: absolute;
  width: ${pxToRem(1)};
  height: ${pxToRem(1)};
  overflow: hidden;
  margin: ${pxToRem(-1)};
  padding: 0;
  border: 0;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
`;
